import { makeAutoObservable, runInAction, reaction } from 'mobx';
import { logger } from '../../utils/debug/logger.js';

const AGE_RANGE_ORDER = [
    "0-10", "11-20", "21-30", "31-40", "41-50",
    "51-60", "61-70", "71-80", "81-90", "91+"
];

const CENTURY_LABELS = {
    's18': '18ème siècle et avant',
    's19': '19ème siècle',
    's20': '20ème siècle',
    's21': '21ème siècle'
};

class StatisticsStore {
    familyStatistics = null;
    individualStatistics = null;
    subscribers = new Set();
    disposers = new Set();
    initialized = false;

    constructor() {
        makeAutoObservable(this, {
            subscribers: false,
            disposers: false,
            initialized: false
        });
    }

    async initialize() {
        logger.statistics.group('📊 Initialisation StatisticsStore');
        if (this.initialized) {
            logger.statistics.debug('Déjà initialisé');
            logger.statistics.groupEnd();
            return;
        }

        try {
            // Import avec gestion d'erreur
            try {
                // Import des dépendances
                logger.statistics.debug('Chargement des dépendances...');
                const [gedcomModule, statisticsServiceModule] = await Promise.all([
                    import('../../gedcom/stores/gedcomDataStore.js'),
                    import('./services/statisticsService.js')
                ]);

                this.gedcomDataStore = gedcomModule.default;
                this.statisticsService = statisticsServiceModule.statisticsService;
            } catch (importError) {
                logger.statistics.error('❌ Erreur lors de l\'import des dépendances:', importError);
                
                // Solution de secours pour obtenir les dépendances
                if (window.gedcomDataStore) {
                    logger.statistics.debug('Utilisation du gedcomDataStore via window');
                    this.gedcomDataStore = window.gedcomDataStore;
                }
                
                if (window.statisticsService) {
                    logger.statistics.debug('Utilisation du statisticsService via window');
                    this.statisticsService = window.statisticsService;
                }
                
                if (!this.gedcomDataStore || !this.statisticsService) {
                    throw new Error('Impossible d\'obtenir les dépendances requises');
                }
            }

            // Vérification de la disponibilité des données
            logger.statistics.debug('Vérification des données Gedcom...');
            if (!this.gedcomDataStore.isFileUploaded) {
                logger.statistics.debug('Pas de fichier GEDCOM chargé, attente...');
                // Observateur pour attendre le chargement du fichier
                const fileUploadDisposer = this.reaction(
                    () => this.gedcomDataStore.isFileUploaded,
                    (isUploaded) => {
                        if (isUploaded) {
                            this.setupHierarchyReaction();
                            fileUploadDisposer();
                        }
                    },
                    { name: 'StatisticsStore-FileUploadReaction' }
                );
                this.disposers.add(fileUploadDisposer);
            } else {
                // Si le fichier est déjà chargé, on configure directement la réaction
                this.setupHierarchyReaction();
            }

            this.initialized = true;
            logger.statistics.debug('✅ Initialisation réussie');

        } catch (error) {
            logger.statistics.error('❌ Erreur lors de l\'initialisation:', error);
            // Créer un état minimal pour éviter d'autres erreurs
            this.familyStatistics = {
                demography: { total: 0, gender: { male: 0, female: 0, unknown: 0 } },
                geography: { birthPlaces: {} },
                family: { marriages: { total: 0 }, children: { average: 0 } }
            };
        }
        logger.statistics.groupEnd();
    }

    // Implémentation minimale de reaction pour le fallback
    reaction(getter, effect, options = {}) {
        // Vérifier si mobx est disponible
        if (typeof mobx !== 'undefined' && mobx.reaction) {
            return mobx.reaction(getter, effect, options);
        }
        
        // Implémentation de secours basique
        logger.statistics.debug(`Création d'une réaction de secours pour ${options.name || 'unknown'}`);
        
        // Première exécution immédiate si demandé
        if (options.fireImmediately) {
            const value = getter();
            effect(value, undefined);
        }
        
        // Retourner une fonction de nettoyage simple
        return () => logger.statistics.debug(`Réaction de secours ${options.name || 'unknown'} nettoyée`);
    }

    setupHierarchyReaction() {
        try {
            // Phase 2: Observer les changements de racine via rootPersonStore
            const rootPersonStore = this.rootPersonStore || window.rootPersonStore;
            
            if (!rootPersonStore) {
                logger.statistics.warn('⚠️ rootPersonStore non disponible pour la réaction');
                return;
            }
            
            const hierarchyDisposer = this.reaction(
                () => ({
                    rootId: rootPersonStore.currentRootId,
                    hierarchy: rootPersonStore.hierarchy,
                    timestamp: Date.now() // Force la réaction même si même personne
                }),
                ({ rootId, hierarchy }) => {
                    if (rootId && hierarchy) {
                        logger.statistics.debug(`📊 Phase 2: Nouveau calcul statistiques pour racine ${rootId}`);
                        if (this.statisticsService && typeof this.statisticsService.processIndividualData === 'function') {
                            this.statisticsService.processIndividualData(rootId, hierarchy);
                        } else {
                            logger.statistics.error('Service de statistiques non disponible ou méthode manquante');
                        }
                    }
                },
                {
                    name: 'StatisticsStore-RootChangeReaction',
                    fireImmediately: false // Ne pas déclencher immédiatement
                }
            );
            this.disposers.add(hierarchyDisposer);
        } catch (error) {
            logger.statistics.error('❌ Erreur lors de la configuration de la réaction de hiérarchie:', error);
        }
    }

    dispose() {
        logger.statistics.debug('Nettoyage du StatisticsStore');
        this.disposers.forEach(disposer => disposer());
        this.disposers.clear();
        this.subscribers.clear();
        this.initialized = false;
        this.familyStatistics = null;
        this.individualStatistics = null;
    }

    updateFamilyStatistics = (newStats) => {
        runInAction(() => {
            if (newStats?.demography?.mortality?.byCentury) {
                logger.statistics.group('Statistiques détaillées par siècle de naissance');
                
                const totalDeathsByCentury = Object.values(newStats.demography.mortality.byCentury)
                    .reduce((sum, century) => sum + century.total, 0);
                
                logger.statistics.group('Vérification de cohérence des données');
                logger.statistics.debug('Total des individus:', newStats.demography.total);
                logger.statistics.debug('Total des décès par siècle:', totalDeathsByCentury);
                
                const individualsWithKnownDates = Object.values(newStats.demography.mortality.byCentury)
                    .reduce((total, century) => {
                        return total + Object.values(century.ageRanges)
                            .reduce((sum, count) => sum + count, 0);
                    }, 0);
                
                logger.statistics.debug('Total des individus avec dates connues:', individualsWithKnownDates);
                
                if (totalDeathsByCentury !== individualsWithKnownDates) {
                    logger.statistics.warn('⚠️ Incohérence dans les totaux de la distribution par âge !');
                    logger.statistics.debug('Différence:', Math.abs(totalDeathsByCentury - individualsWithKnownDates));
                }
                
                const percentageWithDates = ((individualsWithKnownDates / newStats.demography.total) * 100).toFixed(1);
                logger.statistics.debug(`Pourcentage d'individus avec dates connues: ${percentageWithDates}%`);
                logger.statistics.groupEnd();
                
                const totalIndividuals = newStats.demography.total;
                
                Object.entries(newStats.demography.mortality.byCentury)
                    .sort(([a], [b]) => {
                        const order = ['s18', 's19', 's20', 's21'];
                        return order.indexOf(a) - order.indexOf(b);
                    })
                    .forEach(([century, stats]) => {
                        const percentage = ((stats.total / totalIndividuals) * 100).toFixed(1);
                        logger.statistics.group(`${CENTURY_LABELS[century]} (${stats.total} personnes, ${percentage}%)`);
                        
                        if (stats.ageRanges) {
                            const sortedRanges = Object.entries(stats.ageRanges)
                                .filter(([, count]) => count > 0)
                                .sort((a, b) => {
                                    const getOrder = range => {
                                        if (range === "0-1") return -1;
                                        if (range === "1-5") return -0.5;
                                        if (range === "6-10") return 0;
                                        return parseInt(range.split('-')[0]);
                                    };
                                    return getOrder(a[0]) - getOrder(b[0]);
                                });

                            const totalForCentury = stats.total;
                            const averageAge = stats.ages ? 
                                (stats.ages.reduce((sum, age) => sum + age, 0) / stats.ages.length).toFixed(1) : 
                                'N/A';

                            logger.statistics.debug(`Âge moyen au décès: ${averageAge} ans`);
                            logger.statistics.debug('Distribution des âges au décès:');
                            
                            sortedRanges.forEach(([range, count]) => {
                                const rangePercentage = ((count / totalForCentury) * 100).toFixed(1);
                                logger.statistics.debug(`  ${range} ans: ${count} personnes (${rangePercentage}%)`);
                            });
                        }
                        
                        logger.statistics.groupEnd();
                    });

                logger.statistics.groupEnd();
            }

            {
                logger.statistics.group('Métriques principales');
                logger.statistics.debug({
                    'Total des individus': newStats?.demography?.total,
                    'Mariages': newStats?.family?.marriages?.total,
                    'Moyenne d\'enfants': newStats?.family?.children?.average?.toFixed(1)
                });
                logger.statistics.groupEnd();
            }
    
            this.familyStatistics = newStats;
            this.notifySubscribers('family');
        });
    };

    updateIndividualStatistics = (rootId, newStats) => {
        runInAction(() => {
            {
                logger.statistics.group('Updating Individual Statistics');
                logger.statistics.debug('Root ID:', rootId);
                logger.statistics.debug('Individual statistics:', {
                    total: newStats?.demography?.total,
                    gender: newStats?.demography?.gender,
                    ageDistribution: newStats?.demography?.ageDistribution
                });
                logger.statistics.groupEnd();
            }

            this.individualStatistics = newStats;
            this.notifySubscribers('individual');
        });
    };

    resetStatistics = () => {
        runInAction(() => {
            this.familyStatistics = null;
            this.individualStatistics = null;
        });
    };

    getStatistics = (scope = 'current') => {
        const stats = (() => {
            switch (scope) {
                case 'family':
                    return this.familyStatistics;
                case 'individual':
                    return this.individualStatistics;
                case 'current':
                default:
                    return this.individualStatistics || this.familyStatistics;
            }
        })();
    
        if (scope === 'family' && stats?.demography?.ageDistribution) {
            const orderedRanges = AGE_RANGE_ORDER.map(range => ({
                range,
                count: stats.demography.ageDistribution[range] || 0
            }));
    
            logger.statistics.group('Age Distribution Summary');
            logger.statistics.debug(orderedRanges);
            logger.statistics.groupEnd();
        }
    
        return stats;
    };

    getOrderedAgeDistribution = (scope = 'current') => {
        const stats = this.getStatistics(scope);
        if (!stats?.demography?.ageDistribution) return [];
        
        return AGE_RANGE_ORDER.map(range => ({
            range,
            count: stats.demography.ageDistribution[range] || 0
        }));
    };

    getDemographyStats = (scope = 'current') => {
        return this.getStatistics(scope)?.demography;
    };

    getGeographyStats = (scope = 'current') => {
        return this.getStatistics(scope)?.geography;
    };

    getOccupationStats = (scope = 'current') => {
        return this.getStatistics(scope)?.occupations;
    };

    getFamilyStats = (scope = 'current') => {
        return this.getStatistics(scope)?.family;
    };

    getNameStats = (scope = 'current') => {
        return this.getStatistics(scope)?.names;
    };

    getAverageLifespan = (scope = 'current') => {
        return this.getStatistics(scope)?.demography?.lifeExpectancy?.average;
    };

    getGenderDistribution = (scope = 'current') => {
        const gender = this.getStatistics(scope)?.demography?.gender;
        if (!gender) return null;

        const total = Object.values(gender).reduce((a, b) => a + b, 0);
        return {
            male: (gender.male / total * 100).toFixed(1),
            female: (gender.female / total * 100).toFixed(1)
        };
    };

    getAverageChildrenPerCouple = (scope = 'current') => {
        return this.getStatistics(scope)?.family?.children?.average?.toFixed(1);
    };

    getMostCommonBirthPlaces = (limit = 10, scope = 'current') => {
        const birthPlaces = this.getStatistics(scope)?.geography?.birthPlaces;
        if (!birthPlaces) return [];

        return Object.entries(birthPlaces)
            .sort(([,a], [,b]) => b.total - a.total)
            .slice(0, limit);
    };

    getMostCommonOccupations = (limit = 10, scope = 'current') => {
        const occupations = this.getStatistics(scope)?.occupations?.byType;
        if (!occupations) return [];

        return Object.entries(occupations)
            .sort(([,a], [,b]) => b - a)
            .slice(0, limit);
    };

    getMostCommonFirstNames = (gender, limit = 10, scope = 'current') => {
        const names = this.getStatistics(scope)?.names?.firstNames?.[gender];
        if (!names) return [];

        return Object.entries(names)
            .sort(([,a], [,b]) => b - a)
            .slice(0, limit);
    };

    subscribeToUpdates = (callback) => {
        this.subscribers.add(callback);
    };

    notifySubscribers = (scope) => {
        logger.statistics.debug(`Notifying ${this.subscribers.size} subscribers for scope: ${scope}`);
        this.subscribers.forEach(callback => {
            try {
                callback(this.getStatistics(scope), scope);
            } catch (error) {
                logger.statistics.error('Error in statistics subscriber:', error);
            }
        });
    };
}

const statisticsStore = new StatisticsStore();

// Methods to enable/disable debug mode
statisticsStore.enableDebug = () => { statisticsStore.debug = true; };
statisticsStore.disableDebug = () => { statisticsStore.debug = false; };

export default statisticsStore;